// How a project's work depends on itself, for the Debug: Graph tab: its Ready to build
// checks (or its next actions) and tracked dates as nodes, what waits on what as links.
// The checklist records no order of its own beyond a waiting check's ``waits_for``, so a
// link between two checks is worked out from their actions. See
// docs/Y2026-Q4/design-debug-graph.md.
import type {
  NextAction,
  ProjectAgentStatusList,
  WaitNeeds,
} from "../api/hooks/useProjectAgentStatus";
import {
  checkState,
  itemFinished,
  type ProjectItems,
  projectItems,
  resolvingActions,
  shownDue,
  waitItems,
  workedCheck,
} from "./nextActions";

export type GraphMode = "checks" | "actions";
export type LinkKind = "waits" | "serves" | "send";

/** Days after a waited-on item's date that an undated waiting item is drawn. */
const INFERRED_GAP_DAYS = 3;
const DAY_MS = 86_400_000;

export const toDay = (iso: string): number =>
  Math.round(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) / DAY_MS);
export const fromDay = (day: number): string =>
  new Date(day * DAY_MS).toISOString().slice(0, 10);

export interface GraphNode {
  id: string;
  kind: "date" | "check" | "action";
  agent: string;
  title: string;
  /** The day it is drawn on (days since 1970); null when nothing dates it. */
  day: number | null;
  /** Its day is worked out from what it waits on or what works on it, not set on it. */
  inferred: boolean;
  /** Met, or closed: drawn only on request. */
  finished: boolean;
}

/** One action's wait, or a waiting check's own record, that makes a link between checks. */
export interface Because {
  /** The item waited on, and the action (or check) waiting. */
  from: string;
  to: string;
  needs: WaitNeeds | null;
  /** Recorded on the check itself, not worked out from its actions. */
  direct: boolean;
}

export interface GraphLink {
  kind: LinkKind;
  from: string;
  to: string;
  /** What the arrow is labelled with: "if option 1", "if met"; "" when nothing. */
  condition: string;
  /** What it waits for has closed (or been met). */
  satisfied: boolean;
  /** An action's own wait: what it needs of the item. Null on a link between checks. */
  needs: WaitNeeds | null;
  /** Between checks: the waits that make the link. */
  because: Because[];
}

export interface Graph {
  mode: GraphMode;
  today: string;
  items: ProjectItems;
  nodes: Map<string, GraphNode>;
  links: GraphLink[];
  /** Any item's day, an action's included when actions are not drawn. */
  dayOf: (id: string) => ItemDay;
}

interface ItemDay {
  day: number | null;
  inferred: boolean;
}

export const conditionLabel = (needs: WaitNeeds | null): string => {
  if (needs?.kind === "options") return `if option ${needs.options.join("/")}`;
  if (needs?.kind === "outcomes") return `if ${needs.outcomes.join("/")}`;
  return "";
};

/** Each item's day, worked out once: an action's shown date; a check's latest open action;
 * a waiting item's latest prerequisite, a few days on. Inside a loop of waits an item
 * contributes no date, and nothing worked out while a loop was hit is kept, so the result
 * does not depend on which item was asked first. */
const dayResolver = (items: ProjectItems) => {
  const memo = new Map<string, ItemDay>();
  const busy = new Set<string>();
  let loops = 0;
  const latest = (ids: string[]) => {
    const days = ids
      .map((id) => resolve(id).day)
      .filter((d): d is number => d !== null);
    return days.length ? Math.max(...days) : null;
  };
  const compute = (id: string): { day: number | null; inferred: boolean } => {
    const date = items.dates.get(id);
    if (date)
      return { day: date.date.date ? toDay(date.date.date) : null, inferred: false };
    const action = items.actions.get(id)?.action;
    if (action) {
      if (action.closed)
        return {
          day: toDay(action.closed.happened_on || action.closed.at),
          inferred: false,
        };
      const shown = shownDue(action).date;
      if (shown) return { day: toDay(shown), inferred: false };
      const after = latest(waitItems(action).map((w) => w.item));
      return {
        day: after === null ? null : after + INFERRED_GAP_DAYS,
        inferred: true,
      };
    }
    const check = items.checks.get(id)?.check;
    if (!check) return { day: null, inferred: false };
    if (check.met)
      return { day: check.met_on ? toDay(check.met_on) : null, inferred: false };
    const working = resolvingActions(items, id).map((a) => resolve(a.action_id));
    const set = working.filter((r) => r.day !== null && !r.inferred);
    if (set.length) return { day: Math.max(...set.map((r) => r.day as number)), inferred: false };
    const guessed = working.filter((r) => r.day !== null);
    if (guessed.length)
      return { day: Math.max(...guessed.map((r) => r.day as number)), inferred: true };
    return { day: latest(check.waits_for), inferred: true };
  };
  const resolve = (id: string): ItemDay => {
    const known = memo.get(id);
    if (known) return known;
    if (busy.has(id)) {
      loops += 1;
      return { day: null, inferred: true };
    }
    const before = loops;
    busy.add(id);
    const result = compute(id);
    busy.delete(id);
    if (loops === before) memo.set(id, result);
    return result;
  };
  return resolve;
};

/** The graph of one project in either mode. Withdrawn checks are left out; a link to an
 * item the page does not carry is dropped rather than drawn to nowhere. */
export const buildGraph = (
  data: ProjectAgentStatusList,
  mode: GraphMode,
): Graph => {
  const items = projectItems(data);
  const dayOf = dayResolver(items);
  const nodes = new Map<string, GraphNode>();
  const add = (node: Omit<GraphNode, "day" | "inferred">) =>
    nodes.set(node.id, { ...node, ...dayOf(node.id) });

  for (const [id, { agent, date }] of items.dates)
    add({ id, kind: "date", agent, title: date.what, finished: !!date.closed });
  if (mode === "checks") {
    for (const [id, { agent, check }] of items.checks)
      if (!check.closed)
        add({ id, kind: "check", agent, title: check.text, finished: check.met });
  } else {
    for (const [id, { agent, action }] of items.actions)
      add({ id, kind: "action", agent, title: action.title, finished: !!action.closed });
  }

  const links: GraphLink[] = [];
  const byKey = new Map<string, GraphLink>();
  const link = (
    kind: LinkKind,
    from: string,
    to: string,
    extra: Partial<GraphLink> = {},
  ): GraphLink | undefined => {
    if (from === to || !nodes.has(from) || !nodes.has(to)) return undefined;
    const key = `${kind}|${from}|${to}`;
    const existing = byKey.get(key);
    if (existing) return existing;
    const made: GraphLink = {
      kind,
      from,
      to,
      condition: "",
      satisfied: false,
      needs: null,
      because: [],
      ...extra,
    };
    byKey.set(key, made);
    links.push(made);
    return made;
  };

  const actions = [...items.actions.values()].map(({ action }) => action);
  if (mode === "actions") {
    for (const action of actions) {
      for (const wait of waitItems(action))
        link("waits", wait.item, action.action_id, {
          needs: wait.needs,
          condition: conditionLabel(wait.needs),
          satisfied: itemFinished(items, wait.item),
        });
      if (action.serves) link("serves", action.action_id, action.serves);
      // Either side may name the other; the pair is one link.
      for (const other of action.send_with) {
        const [first, second] = [action.action_id, other].sort();
        link("send", first, second);
      }
    }
  } else {
    // The node an item stands for in this view: an open action stands for the check it
    // works towards, read as the checklist and the pass gate read it (``workedCheck``); a
    // closed one for nothing; a date or a check for itself.
    const checkOf = (action: NextAction) => workedCheck(items, action)?.check_id;
    const nodeFor = (id: string): string | undefined => {
      const action = items.actions.get(id)?.action;
      return action ? checkOf(action) : id;
    };
    const because = (from: string, to: string, why: Because, finished: boolean) => {
      const made = link("waits", from, to, { satisfied: true });
      if (!made) return;
      made.because.push(why);
      made.satisfied = made.satisfied && finished;
    };
    for (const action of actions) {
      const target = checkOf(action);
      if (!target) continue;
      for (const wait of waitItems(action)) {
        const source = nodeFor(wait.item);
        if (source)
          because(
            source,
            target,
            { from: wait.item, to: action.action_id, needs: wait.needs, direct: false },
            itemFinished(items, wait.item),
          );
      }
      if (action.serves) link("serves", target, action.serves);
    }
    for (const [id, { check }] of items.checks)
      for (const item of check.waits_for) {
        const source = nodeFor(item);
        if (source)
          because(
            source,
            id,
            { from: item, to: id, needs: null, direct: true },
            itemFinished(items, item),
          );
      }
    // A label only when every wait behind the link carries a condition.
    for (const made of links) {
      const labels = made.because.map((b) => conditionLabel(b.needs));
      if (labels.length && labels.every(Boolean))
        made.condition = [...new Set(labels)].join(" / ");
    }
  }
  return { mode, today: data.lists_today, items, nodes, links, dayOf };
};

/** A wait whose waiting item is dated before what it waits on, both dates set rather than
 * worked out. Between checks it is judged on the actions behind the link, not on the two
 * checks' dates: a check sits on its latest action, which says nothing of the one that
 * waits. */
export const outOfOrderWaits = (
  graph: Graph,
  link: GraphLink,
): { from: string; to: string }[] => {
  if (link.kind !== "waits" || link.satisfied) return [];
  const pairs = link.because.length
    ? link.because.filter((b) => !b.direct).map((b) => ({ from: b.from, to: b.to }))
    : [{ from: link.from, to: link.to }];
  return pairs.filter(({ from, to }) => {
    const f = graph.dayOf(from);
    const t = graph.dayOf(to);
    return (
      f.day !== null && t.day !== null && !f.inferred && !t.inferred && t.day < f.day
    );
  });
};

export const outOfOrder = (graph: Graph, link: GraphLink): boolean =>
  outOfOrderWaits(graph, link).length > 0;

/** What a selection lights: the items upstream and downstream of ``ids`` through waits,
 * and their direct date and email neighbours. */
export const relatedTo = (graph: Graph, ids: string[]): Set<string> => {
  const out = new Set(ids);
  const walk = (up: boolean) => {
    const stack = [...ids];
    const seen = new Set(ids);
    while (stack.length) {
      const at = stack.pop() as string;
      for (const l of graph.links) {
        if (l.kind !== "waits") continue;
        const [here, next] = up ? [l.to, l.from] : [l.from, l.to];
        if (here === at && !seen.has(next)) {
          seen.add(next);
          out.add(next);
          stack.push(next);
        }
      }
    }
  };
  walk(true);
  walk(false);
  for (const l of graph.links)
    if (l.kind !== "waits") {
      if (ids.includes(l.from)) out.add(l.to);
      if (ids.includes(l.to)) out.add(l.from);
    }
  return out;
};

/** Everything downstream of one item through open waits. */
const downstream = (graph: Graph, id: string): Set<string> => {
  const out = new Set<string>();
  const stack = [id];
  while (stack.length) {
    const at = stack.pop() as string;
    for (const l of graph.links)
      if (l.kind === "waits" && !l.satisfied && l.from === at && !out.has(l.to)) {
        out.add(l.to);
        stack.push(l.to);
      }
  }
  return out;
};

/** Previewing a decision's answer: each action waiting on it would go ahead, or need a
 * look because it waits on another option. */
export const previewAnswer = (
  graph: Graph,
  decisionId: string,
  option: number,
): Map<string, "go" | "look"> =>
  new Map(
    graph.links
      .filter((l) => l.kind === "waits" && l.from === decisionId)
      .map((l) => [
        l.to,
        l.needs?.kind === "options" && !l.needs.options.includes(option)
          ? "look"
          : "go",
      ]),
  );

export interface Finding {
  tone: "red" | "amber" | "info";
  /** The item it opens; null for a count over the project. */
  id: string | null;
  /** The item's name, shown bold, then the rest of the sentence. */
  lead: string;
  text: string;
}

const itemTitle = (items: ProjectItems, id: string): string | undefined =>
  items.actions.get(id)?.action.title ??
  items.dates.get(id)?.date.what ??
  items.checks.get(id)?.check.text;

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

/** What a debugging operator should look at first, most serious first. */
export const findings = (graph: Graph): Finding[] => {
  const { items, nodes } = graph;
  const red: Finding[] = [];
  const amber: Finding[] = [];
  const info: Finding[] = [];
  const name = (id: string) => nodes.get(id)?.title ?? id;
  const when = (day: number | null) => (day === null ? "no date" : fromDay(day));

  // Named by the actions themselves, which may not be drawn in this view.
  const title = (id: string) => itemTitle(items, id) ?? name(id);
  for (const l of graph.links)
    for (const w of outOfOrderWaits(graph, l))
      red.push({
        tone: "red",
        id: l.to,
        lead: title(w.to),
        text: `is dated ${when(graph.dayOf(w.to).day)} but waits for "${title(w.from)}", dated ${when(graph.dayOf(w.from).day)}. One of the dates is wrong.`,
      });

  const open = [...items.actions.values()].filter(({ action }) => !action.closed);
  if (graph.mode === "checks") {
    const checks = [...items.checks.values()]
      .map(({ check }) => check)
      .filter((c) => !c.closed);
    for (const check of checks) {
      const state = checkState(check, items);
      if (state === "nobody")
        red.push({ tone: "red", id: check.check_id, lead: check.text, text: "has nobody on it, and no reason given." });
      if (state === "wait_ended")
        red.push({ tone: "red", id: check.check_id, lead: check.text, text: "was waiting, but everything it waited for has closed." });
      if (resolvingActions(items, check.check_id).some((a) => a.status?.late))
        red.push({ tone: "red", id: check.check_id, lead: check.text, text: "is held up by a late action." });
    }
    const unlinked = open.filter(({ action }) => !workedCheck(items, action));
    if (unlinked.length)
      red.push({
        tone: "red",
        id: null,
        lead: plural(unlinked.length, "open action"),
        text: `work towards no open checklist item, so this view leaves them out: ${unlinked.map(({ action }) => `"${action.title}"`).join(", ")}. The pass gate should send the agent back.`,
      });
    const forAction = checks.filter((c) => !c.met && c.basis === "action");
    if (forAction.length)
      amber.push({
        tone: "amber",
        id: forAction[0].check_id,
        lead: plural(forAction.length, "item"),
        text: `added only so an action had somewhere to sit: ${forAction.map((c) => `"${c.text}"`).join(", ")}. Worth challenging.`,
      });
    const undated = checks.filter((c) => !c.met && nodes.get(c.check_id)?.day === null);
    if (undated.length)
      info.push({ tone: "info", id: undated[0].check_id, lead: plural(undated.length, "open item"), text: "have nothing to date them by, so the Timeline puts them under No date." });
  } else {
    for (const { action } of open)
      if (action.status?.late)
        red.push({ tone: "red", id: action.action_id, lead: action.title, text: `is late${action.blocking ? " and blocking" : ""}.` });
    for (const { action } of open) {
      if (action.type !== "decision") continue;
      const deps = graph.links.filter((l) => l.kind === "waits" && l.from === action.action_id);
      if (deps.some((l) => l.needs?.kind === "options"))
        info.push({ tone: "info", id: action.action_id, lead: action.title, text: `decides what happens to ${plural(deps.length, "action")}; preview each answer from it.` });
    }
    const placed = open.filter(({ action }) => {
      const n = nodes.get(action.action_id);
      return n?.inferred && n.day !== null;
    });
    if (placed.length)
      info.push({ tone: "info", id: placed[0].action.action_id, lead: plural(placed.length, "waiting action"), text: "have no date yet; the Timeline draws them just after what they wait on." });
    const undated = open.filter(({ action }) => nodes.get(action.action_id)?.day === null);
    if (undated.length)
      info.push({ tone: "info", id: undated[0].action.action_id, lead: plural(undated.length, "open action"), text: "have nothing to date them by, so the Timeline puts them under No date." });
  }

  const unfinished = [...nodes.values()].filter((n) => !n.finished && n.kind !== "date");
  const top = unfinished
    .map((n) => ({ n, below: downstream(graph, n.id) }))
    .sort((a, b) => b.below.size - a.below.size)[0];
  if (top && top.below.size > 1) {
    const agents = new Set([...top.below].map((id) => nodes.get(id)?.agent));
    info.push({ tone: "info", id: top.n.id, lead: top.n.title, text: `holds up ${plural(top.below.size, "other item")} across ${plural(agents.size, "agent")}.` });
  }
  const undatedDates = [...items.dates.values()].filter(({ date }) => !date.closed && !date.date);
  if (undatedDates.length)
    info.push({ tone: "info", id: null, lead: plural(undatedDates.length, "tracked date"), text: "have no date set, so they are not drawn." });
  const waits = graph.links.filter((l) => l.kind === "waits" && !l.satisfied);
  const cross = waits.filter((l) => nodes.get(l.from)?.agent !== nodes.get(l.to)?.agent);
  info.push({ tone: "info", id: null, lead: `${cross.length} of ${waits.length}`, text: "open links cross from one agent to another." });
  return [...red, ...amber, ...info];
};
